import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  take,
} from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/app/environments/environment';
import {
  AddLocationGroupPayload,
  AddLocationGroupResponse,
  CurrentLocation,
  GetAllCountries,
  GetAllTimezones,
  GetLinkedSitesByGroup,
  GetLocationGroups,
  GetLocationProfile,
  GetLocationsByGroup,
  LocationGroup,
  LocationProfile,
  PostLocationProfileResponse,
  UpdateLocationGroupPayload,
  UpdateLocationGroupResponse,
} from '../../../models/admin/location-admin.model';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  baseUrl = environment.apiUrl.concat('/location/');

  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  // private subject for tracking current location
  private currentLocationSubj$: BehaviorSubject<CurrentLocation> =
    new BehaviorSubject({
      groupCode: '',
    } as CurrentLocation);

  getCurrentLocation$(): Observable<CurrentLocation> {
    return this.currentLocationSubj$.asObservable();
  }

  setCurrentLocation$(newValue: CurrentLocation) {
    this.currentLocationSubj$.next(newValue);
  }

  // private subject for tracking location profile changes
  private currentProfileState$: BehaviorSubject<LocationProfile> =
    new BehaviorSubject({} as LocationProfile);

  getProfileState$(): Observable<LocationProfile> {
    return this.currentProfileState$.asObservable();
  }

  setProfileState$(newValue: LocationProfile) {
    this.currentProfileState$.next(newValue);
  }

  // private subject for providing location groups
  private locationGroups$: BehaviorSubject<LocationGroup[]> =
    new BehaviorSubject([] as LocationGroup[]);

  getLocationGroups$(): Observable<LocationGroup[]> {
    return this.locationGroups$.asObservable();
  }

  setLocationGroups$(newValue: LocationGroup[]) {
    this.locationGroups$.next(newValue);
  }

  // private subject for providing edited location value
  private editedLocation$ = new BehaviorSubject<LocationProfile | null>(null);

  getEditedLocation$(): Observable<LocationProfile | null> {
    return this.editedLocation$.asObservable();
  }

  setEditedLocation$(newValue: LocationProfile) {
    this.editedLocation$.next(newValue);
  }
  /**
   *Get a list of all the available location groups
   *
   * @return {*}  {Observable<GetLocationGroups>}
   * @memberof LocationService
   */
  getLocationGroups(): Observable<GetLocationGroups> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetLocationGroups>(this.baseUrl.concat('get-location-groups'))
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetLocationGroups>(
            error,
            error.status,
            'Location Service: Get LocationGroups',
            BANNER_TYPE.LOCATION_GROUPS_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a list of all available countries
   *
   * @return {*}  {Observable<GetAllCountries>}
   * @memberof LocationService
   */
  getAllCountries(): Observable<GetAllCountries> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetAllCountries>(this.baseUrl.concat('get-all-countries'))
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetAllCountries>(
            error,
            error.status,
            'Location Service: Get All Countries',
            BANNER_TYPE.COUNTRY_ERROR,
          ),
        ),
        map((val) => {
          const regex = /^[a-zA-Z]+$/;
          const newResponse = val.response.country.filter(
            (val) => val.isoCode.length > 1 && regex.test(val.isoCode),
          );
          return { ...val, response: { country: newResponse } };
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a list of all available timezones
   *
   * @return {*}  {Observable<GetAllTimezones>}
   * @memberof LocationService
   */
  getAllTimezones(): Observable<GetAllTimezones> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetAllTimezones>(this.baseUrl.concat('get-all-timezones'))
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetAllTimezones>(
            error,
            error.status,
            'Location Service: Get All Timezones',
            BANNER_TYPE.TIMEZONE_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a list of all locations associated with the passed in location group or all locations if null is passed in
   *
   * @param {string} groupCode
   * @return {*}  {Observable<GetLocationsByGroup>}
   * @memberof LocationService
   */
  getLocationsByGroup(
    groupCode: number | null,
    isAdminSearch: boolean,
  ): Observable<GetLocationsByGroup> {
    this.commonUtils.updateLoadingModal(true);
    let obs$;
    if (groupCode === null && isAdminSearch) {
      obs$ = this.http.get<GetLocationsByGroup>(
        this.baseUrl.concat('get-locations-per-group'),
        { params: { isAdminSearch: isAdminSearch } },
      );
    } else if (groupCode !== null && !isAdminSearch) {
      obs$ = this.http.get<GetLocationsByGroup>(
        this.baseUrl.concat('get-locations-per-group'),
        { params: { groupId: groupCode, isAdminSearch: false } },
      );
    } else {
      obs$ = of();
    }

    return obs$.pipe(
      take(1),
      catchError((error) =>
        this.errorHandler.handleError<GetLocationsByGroup>(
          error,
          error.status,
          'Location Service: Get Locations By Group',
          BANNER_TYPE.TABLE_DATA_ERROR,
        ),
      ),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  /**
   *Get a specific location based on the passed in location code
   *
   * @param {string} _locationCode
   * @return {*}  {Observable<GetLocationProfile>}
   * @memberof LocationService
   */
  getLocationProfile(
    locationCode: string | null,
  ): Observable<GetLocationProfile> {
    this.commonUtils.updateLoadingModal(true);
    let observable$;
    if (locationCode !== null) {
      observable$ = this.http.get<GetLocationProfile>(
        this.baseUrl.concat('location-config'),
        {
          params: { locationCode: locationCode },
        },
      );
    } else {
      observable$ = this.http.get<GetLocationProfile>(
        this.baseUrl.concat('location-config'),
      );
    }
    return observable$.pipe(
      take(1),
      catchError((error) =>
        this.errorHandler.handleError<GetLocationProfile>(
          error,
          error.status,
          'Location Service: Get Location Profile',
          BANNER_TYPE.LOCATION_PROFILE_ERROR,
        ),
      ),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  /**
   *Get all linked sites for the passed in location groups
   *
   * @param {string} locationGroup
   * @return {*}  {Observable<GetLinkedSitesByGroup>}
   * @memberof LocationService
   */
  getLinkedSitesByGroup(
    locationGroup: string,
    showLoading = true,
    errorBanner = BANNER_TYPE.GROUP_LINKED_SITES_ERROR,
  ): Observable<GetLinkedSitesByGroup> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetLinkedSitesByGroup>(this.baseUrl.concat('get-linked-sites'), {
        params: { groupCode: locationGroup },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetLinkedSitesByGroup>(
            error,
            error.status,
            'Location Service: Get Linked Sites By Group',
            errorBanner,
          ),
        ),
        finalize(() => {
          if (showLoading) {
            this.commonUtils.updateLoadingModal(false);
          }
        }),
      );
  }

  /**
   * Add location for given location profile
   *
   * @param {LocationProfile} location
   * @param {string} code
   * @return {*}  {Observable<PostLocationProfileResponse>}
   * @memberof LocationService
   */
  addLocation(
    location: LocationProfile,
  ): Observable<PostLocationProfileResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<PostLocationProfileResponse>(
        this.baseUrl.concat('add-location'),
        location,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<PostLocationProfileResponse>(
            error,
            error.error.code,
            'Location Service: Add Location',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Update location for given location profile
   *
   * @param {LocationProfile} location
   * @param {string} code
   * @return {*}  {Observable<PostLocationProfileResponse>}
   * @memberof LocationService
   */
  updateLocation(
    location: LocationProfile,
    code: string,
  ): Observable<PostLocationProfileResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<PostLocationProfileResponse>(
        this.baseUrl.concat('update-location'),
        location,
        {
          params: { locationCode: code },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<PostLocationProfileResponse>(
            error,
            error.status,
            'Location Service: Update Location',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Update location group for given locationGroup
   *
   * @param {string} locationGroup
   * @param {UpdateLocationGroupPayload} body
   * @return {*}  {Observable<UpdateLocationGroupResponse>}
   * @memberof LocationService
   */
  updateLocationGroup(
    locationGroup: string,
    body: UpdateLocationGroupPayload,
  ): Observable<UpdateLocationGroupResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .put<UpdateLocationGroupResponse>(
        `${this.baseUrl.concat(
          'update-location-group',
        )}?locationGroup=${locationGroup}`,
        body,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<UpdateLocationGroupResponse>(
            error,
            error.status,
            'Location Service: Update Location Group',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
        filter((value) => !!value),
      );
  }

  /**
   *Create a new location group with the passed in location group payload
   *
   * @param {AddLocationGroupPayload} body
   * @return {*}
   * @memberof LocationService
   */
  addLocationGroup(body: AddLocationGroupPayload) {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<AddLocationGroupResponse>(
        `${this.baseUrl.concat('add-location-group')}`,
        body,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<AddLocationGroupResponse>(
            error,
            error.status,
            'Location Service: Add Location Group',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Delete a location group with the passed in location group payload
   *
   * @param {AddLocationGroupPayload} body
   * @return {*}
   * @memberof LocationService
   */
  deleteLocationGroup(group: LocationGroup, deletedById: string) {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .delete<AddLocationGroupResponse>(
        `${this.baseUrl.concat('delete-location-group')}`,
        {
          headers: { loginId: deletedById },
          params: { groupId: group.id },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<AddLocationGroupResponse>(
            error,
            error.status,
            'Location Service: Delete Location Group',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
        filter((value) => !!value),
      );
  }
}
